import axios from 'axios';

import apiWrapper from 'common/api/api-wrapper';
import { IAPIErrorContent } from 'common/api/type-def';
import { CONTENT_TYPE } from 'common/constants';
import { getHeader } from 'common/utils/utilFunctions';

import {
	IChatSessionAPIResponse,
	IChatGetNameResponse,
	IChatHistory,
	IHighlight,
	IHighlightPayload,
	IResponseGPTMessageData,
	IPromptResponse,
	ISaveRecordPayload,
	ISaveRecordResponse,
	IDeleteDocumentRequestParams,
	IDeleteDocumentResponseParams,
	IKnowledgeBaseOptions,
	IUserPromptsConfig,
	IChatRecords,
	IFailedUploadedFilesResponse,
} from './type-def';

const genAiServiceUrl = process.env.REACT_APP_GEN_AI_SERVICE_URL;
const regChangeAPI = process.env.REACT_APP_REG_CHANGE_DOMAIN_SERVICE_URL;

const { JSON } = CONTENT_TYPE;

export const createChatSession = async (
	chatName: string,
	knowledgeBase: string,
) => {
	const headers = {
		configuration: {
			session_type: knowledgeBase,
		},
		name: chatName,
	};
	const resp = await apiWrapper.post<
		IChatSessionAPIResponse | IAPIErrorContent
	>(`${genAiServiceUrl}/chat/sessions`, headers, JSON, getHeader());
	return resp;
};

export const getChatName = async (id: string, prompt: string) => {
	const payload = {
		base_prompt: prompt,
	};
	const resp = await apiWrapper.patch<IChatGetNameResponse | IAPIErrorContent>(
		`${genAiServiceUrl}/chat/sessions/${id}/autorename`,
		payload,
		JSON,
		getHeader(),
	);
	return resp;
};

export const updateChatName = async (id: string, name: string) => {
	const payload = {
		name,
	};
	const resp = await apiWrapper.patch<
		IChatSessionAPIResponse | IAPIErrorContent
	>(`${genAiServiceUrl}/chat/sessions/${id}`, payload, JSON, getHeader());
	return resp;
};

export const sendMessageToGenAPI = async (
	prompt: string,
	id: string,
	role?: string,
) => {
	const payload = {
		component: 'prompt',
		prompt,
		...(role ? { prompt_role: role } : {}),
	};
	const resp = await apiWrapper.post<
		IResponseGPTMessageData | IAPIErrorContent
	>(
		`${genAiServiceUrl}/chat/sessions/${id}/messages`,
		payload,
		JSON,
		getHeader(),
	);
	return resp;
};

export const getChartHistory = async () => {
	const resp = await apiWrapper.get<IChatHistory[] | IAPIErrorContent>(
		`${genAiServiceUrl}/chat/sessions`,
		{},
		JSON,
		getHeader(),
	);
	return resp;
};

export const getGPTMessagesResponse = async (id: string) => {
	const resp = await apiWrapper.get<IResponseGPTMessageData[]>(
		`${genAiServiceUrl}/chat/sessions/${id}/messages`,
		{},
		JSON,
		getHeader(),
	);
	return resp;
};

export const getSessionData = async (id: string) => {
	const resp = await apiWrapper.get<IChatSessionAPIResponse | IAPIErrorContent>(
		`${genAiServiceUrl}/chat/sessions/${id}`,
		{},
		JSON,
		getHeader(),
	);
	return resp;
};

export const updateChatHistoryName = async (
	sessionId: string,
	name: string,
) => {
	const resp = await apiWrapper.patch<IChatSessionAPIResponse>(
		`${genAiServiceUrl}/chat/sessions/${sessionId}`,
		{ name },
		JSON,
		getHeader(),
	);
	return resp;
};

export const getChartSearchHistory = async (searchText: string) => {
	const resp = await apiWrapper.post<IChatHistory[] | IAPIErrorContent>(
		`${genAiServiceUrl}/chat/sessions/search`,
		{ text: searchText },
		JSON,
		getHeader(),
	);
	return resp;
};

export const deleteChartHistory = async (sessionId: string) => {
	const resp = await apiWrapper.delete<IChatHistory | IAPIErrorContent>(
		`${genAiServiceUrl}/chat/sessions/${sessionId}`,
		null,
		{},
		JSON,
		getHeader(),
	);
	return resp;
};

export const getPreGeneratedPrompts = async (
	role: string | null,
	defaultPrompt: boolean,
) => {
	const resp = await apiWrapper.get<IPromptResponse | IAPIErrorContent>(
		`${genAiServiceUrl}/user_prompts`,
		{
			...(role ? { role } : null),
			...(defaultPrompt ? { defaultOnly: defaultPrompt } : {}),
		},
		JSON,
		getHeader(),
	);
	return resp;
};

export const getKnowledgeBaseOptions = async () => {
	const resp = await apiWrapper.get<IKnowledgeBaseOptions[] | IAPIErrorContent>(
		`${regChangeAPI}/ui/config/ask-risk-link`,
		{},
		JSON,
		getHeader(),
	);
	return resp;
};

export const deleteChatSessionDocument = async (
	data: IDeleteDocumentRequestParams,
) => {
	const resp = await apiWrapper.delete<
		IDeleteDocumentResponseParams | IAPIErrorContent
	>(
		`${genAiServiceUrl}/chat/sessions/${data.sessionId}/documents/${data?.id}`,
		null,
		{},
		JSON,
		getHeader(),
	);
	return resp;
};

export const getFileFromBlob = async (
	blobUrl: string,
	fileName: string,
	type: string,
) => {
	const config = { data: { responseType: 'blob' } };
	const resp = await axios.get(blobUrl, config);
	const file = new File([resp.data], fileName, { type });
	return file;
};

export const getUserPromptsConfig = async (type: string) => {
	const resp = await apiWrapper.get<IUserPromptsConfig[] | IAPIErrorContent>(
		`${genAiServiceUrl}/user_prompts/config?type=${type}`,
		{},
		JSON,
		getHeader(),
	);
	return resp;
};

export const getChatRecords = async () => {
	const resp = await apiWrapper.get<IChatRecords[] | IAPIErrorContent>(
		`${genAiServiceUrl}/chat/records`,
		{},
		JSON,
		getHeader(),
	);
	return resp;
};

export const getChatRecordById = async (id: string) => {
	const resp = await apiWrapper.get<IChatRecords | IAPIErrorContent>(
		`${genAiServiceUrl}/chat/records/${id}`,
		{},
		JSON,
		getHeader(),
	);
	return resp;
};

export const fetchHighlights = async (payload: IHighlightPayload) => {
	const resp = await apiWrapper.post<IHighlight[] | [], IAPIErrorContent>(
		`${genAiServiceUrl}/chat/records/generate-highlights/execute`,
		payload,
		JSON,
		getHeader(),
	);
	return resp;
};

export const saveChatRecord = async (payload: ISaveRecordPayload) => {
	const resp = await apiWrapper.post<ISaveRecordResponse, IAPIErrorContent>(
		`${genAiServiceUrl}/chat/records`,
		payload,
		JSON,
		getHeader(),
	);
	return resp;
};

export const deleteLatestFailedFilesUploadedDocument = async (
	id: string,
	fileNames: string[],
) => {
	const resp = await apiWrapper.post<
		IFailedUploadedFilesResponse[] | IAPIErrorContent
	>(
		`${genAiServiceUrl}/chat/sessions/${id}/documents/delete/latest`,
		{ file_names: fileNames },
		JSON,
		getHeader(),
	);
	return resp;
};

export const deleteChatRecordById = async (id: string) => {
	const resp = await apiWrapper.delete<any | IAPIErrorContent>(
		`${genAiServiceUrl}/chat/records/${id}`,
		null,
		{},
		JSON,
		getHeader(),
	);
	return resp;
};
